import React, { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import i18next from 'i18next';
import { Repeat, RepeatTiny } from '../../StyledComponents/Repeat';
import ActionList from './ActionList';
import { ButtonInline } from '../../StyledComponents/Button';
import { StateIndicator } from '../../StyledComponents/StateIndicator';
import { UtilityFontSizeSmall } from '../../StyledComponents/Utility';
import { Columns, Column } from '../../StyledComponents/Columns';
import CheckMarkList, { CheckMarkListItem } from '../../StyledComponents/CheckMarkList';
import Content from '../../StyledComponents/Content';
import SwitchBar from '../../StyledComponents/SwitchBar';
import {
  Manufacturer,
  PackageOption,
  Ecommerce,
  ContactOptions,
  Branch,
  Vehicle,
} from '../../@types/codegen/types';
import BranchModal from './BranchModal';
import PackageOptionModal, { PackageOptionModalData } from './PackageOptionModal';
import { ConversionOption } from '../../@types/Search';
interface CheckList {
  vehicle: Vehicle;
  manufacturer?: Manufacturer | null;
  packageOptions: PackageOption[];
  ecommerce?: Ecommerce | null;
  branch?: Branch | null;
  contact?: ContactOptions | null;
  loadingCentralStorageVehicle: boolean;
  conversionOptions?: ConversionOption[];
  availableFrom?: Date | null;
  startEcom: () => void;
}

const CheckList = ({
  vehicle,
  manufacturer,
  packageOptions,
  ecommerce,
  branch,
  contact,
  conversionOptions,
  availableFrom,
  loadingCentralStorageVehicle,
  startEcom,
}: CheckList) => {
  const [modalBranch, setModalBranch] = useState(false);
  const openModalBranch = useCallback(() => setModalBranch(true), []);
  const closeModalBranch = useCallback(() => setModalBranch(false), []);

  const [modal, setModal] = useState<PackageOptionModalData>();
  const onOpen = useCallback((nextModal: PackageOptionModalData) => setModal(nextModal), []);
  const onClose = useCallback(() => setModal(undefined), []);

  useEffect(() => {
    if ((branch || contact) && modalBranch) {
      setModalBranch(false);
    }
  }, [branch, contact]);

  const packageOption = manufacturer?.packageOption;

  return (
    <>
      {modalBranch && (
        <BranchModal
          loading={loadingCentralStorageVehicle}
          connections={branch?.connections}
          onClose={closeModalBranch}
        />
      )}
      {modal && <PackageOptionModal packageOption={modal} onClose={onClose} />}
      <Repeat>
        {ecommerce?.reserved && (
          <RepeatTiny>
            <SwitchBar
              title={i18next.t('item.carIsReserved')}
              body={i18next.t('item.carIsReservedDescription') || undefined}
            >
              <Content>
                <p>{i18next.t('item.carIsReservedModalBody')}</p>
              </Content>
            </SwitchBar>
          </RepeatTiny>
        )}
        <ActionList
          ecommerce={ecommerce}
          branch={branch}
          conversionOptions={conversionOptions}
          contact={contact}
          vehicle={vehicle}
          startEcom={startEcom}
        />
      </Repeat>
      {(branch?.connections.length || 0) > 1 && (
        <Repeat>
          <SwitchBar
            title={i18next.t('item.centralWarehouseCar')}
            actionTitle={i18next.t('item.switchBranch') || ''}
            onClick={openModalBranch}
          >
            <Content>
              <p>{i18next.t('item.centralWarehouseCarDescription')}</p>
            </Content>
          </SwitchBar>
        </Repeat>
      )}
      <Repeat>
        {availableFrom && new Date(availableFrom).valueOf() > new Date().valueOf() && (
          <RepeatTiny>
            <Columns $spacing={1} valign="center">
              <Column noShrink>
                <StateIndicator $tone="pending" />
              </Column>
              <Column>
                <UtilityFontSizeSmall>
                  {i18next.t('item.availableFromDate', { date: format(new Date(availableFrom), 'dd MMMM yyyy', { locale: sv }) })}
                </UtilityFontSizeSmall>
              </Column>
            </Columns>
          </RepeatTiny>
        )}
        <RepeatTiny>
          <CheckMarkList>
            {packageOption?.title && (
              <CheckMarkListItem>
                <>
                  {i18next.t('item.includingShort')}{' '}
                  <ButtonInline
                    onClick={() =>
                      onOpen({
                        title: packageOption?.title,
                        image: packageOption?.image,
                        description: packageOption?.description,
                        link: packageOption?.link,
                      })
                    }
                  >
                    {packageOption.title}
                  </ButtonInline>{' '}
                  {i18next.t('item.usedWarrany').toLowerCase()}
                </>
              </CheckMarkListItem>
            )}
            {packageOptions?.map((packageOption, index) => (
              <CheckMarkListItem key={packageOption.title || index}>
                <>
                  {i18next.t('item.includingShort')}{' '}
                  <ButtonInline
                    onClick={() =>
                      onOpen({
                        title: packageOption?.title,
                        image: packageOption?.image,
                        description: packageOption?.description,
                        link: packageOption?.link,
                      })
                    }
                  >
                    {packageOption.title}
                  </ButtonInline>
                </>
              </CheckMarkListItem>
            ))}
            {ecommerce?.withHomeDelivery && <CheckMarkListItem>{i18next.t('item.homeDelivery')}</CheckMarkListItem>}
          </CheckMarkList>
        </RepeatTiny>
      </Repeat>
    </>
  );
};

export default CheckList;
